import React, { useState, useEffect } from 'react';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import { Link, useNavigate } from 'react-router-dom';
import { Package, ShoppingCart } from 'lucide-react';

const ShopSection = () => {
    const [products, setProducts] = useState<any[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [activeCategory, setActiveCategory] = useState<string>('Tous');
    const [loading, setLoading] = useState(false);
    const user = useAuthStore(state => state.user);
    const navigate = useNavigate();
    
    // Checkout state
    const [selectedProduct, setSelectedProduct] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [checkoutForm, setCheckoutForm] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
        phone: '',
        address: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);

    useEffect(() => {
        fetchProducts();
    }, []);

    useEffect(() => {
        if (user) {
            setCheckoutForm(prev => ({
                ...prev,
                firstName: user.firstName || prev.firstName,
                lastName: user.lastName || prev.lastName,
                email: user.email || prev.email
            }));
        }
    }, [user]);


    const fetchProducts = async () => {
        setLoading(true);
        try {
            const response = await api.get('/products');
            const data = response.data || [];
            
            setProducts(data);
            
            // Extract unique categories
            const cats = Array.from(new Set(data.map((p: any) => p.category).filter(Boolean)));
            setCategories(['Tous', ...(cats as string[])]);
        } catch (error) {
            console.error('Erreur lors du chargement des produits', error);
            setProducts([]);
            setCategories(['Tous']);
        } finally {
            setLoading(false);
        }
    };

    const filteredProducts = activeCategory === 'Tous' 
        ? products 
        : products.filter(p => p.category === activeCategory);

    const handleBuyClick = (product: any) => {
        setSelectedProduct(product);
        setIsModalOpen(true);
        setOrderSuccess(false);
    };

    const handleCheckoutSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const orderPayload = {
                product_id: selectedProduct.id,
                client_info: checkoutForm,
                total_amount: selectedProduct.price,
                status: 'pending'
            };
            await api.post('/orders', orderPayload);
            setOrderSuccess(true);
            setTimeout(() => {
                setIsModalOpen(false);
                setSelectedProduct(null);
            }, 3000);
        } catch (error) {
            console.error("Erreur lors de la commande", error);
            alert("Une erreur est survenue lors de la commande.");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <section className="site-section bg-[#121212]" id="shop-section">
            <div className="container">
                <div className="row justify-content-center text-center mb-5">
                    <div className="col-md-8">
                        <span className="subheading font-extrabold uppercase tracking-widest text-sm mb-2 block" style={{ color: '#eab308' }}>
                            NOTRE BOUTIQUE
                        </span>
                        <h2 className="heading mb-3 font-extrabold text-4xl text-white">Produits & Accessoires</h2>
                        <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto">
                            Equipez-vous avec le meilleur matériel et atteignez vos objectifs avec nos compléments nutritionnels sélectionnés par nos experts.
                        </p>
                    </div>
                </div>

                {/* Categories Filter */}
                <div className="flex flex-wrap justify-center gap-3 mb-10">
                    {categories.map(cat => (
                        <button
                            key={cat}
                            onClick={() => setActiveCategory(cat)}
                            className={`px-6 py-2 rounded-full font-bold transition-all duration-300 ${
                                activeCategory === cat 
                                ? 'bg-yellow-500 text-black shadow-[0_4px_15px_rgba(234,179,8,0.4)]' 
                                : 'bg-[#1a1a1a] text-gray-400 hover:text-yellow-500 hover:bg-[#222]'
                            }`}
                        >
                            {cat}
                        </button>
                    ))}
                </div>

                {/* Products Grid */}
                {loading ? (
                    <div className="flex justify-center items-center py-20">
                        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-500"></div>
                    </div>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-5">
                        {filteredProducts.map(product => (
                            <div key={product.id} className="bg-[#1a1a1a] rounded-2xl border border-gray-800 overflow-hidden hover:border-yellow-500 transition-colors duration-300 group flex flex-col h-full">
                                <div className="h-32 sm:h-48 overflow-hidden relative shrink-0">
                                    {product.image_url ? (
                                        <img src={product.image_url} alt={product.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    ) : (
                                        <div className="w-full h-full bg-gray-800 flex items-center justify-center text-gray-500">
                                            <Package className="w-8 h-8 sm:w-10 sm:h-10" />
                                        </div>
                                    )}
                                    <div className="absolute top-2 right-2 sm:top-3 sm:right-3 bg-black/70 backdrop-blur-md px-2 py-1 rounded-full text-[9px] sm:text-[10px] font-bold text-yellow-500 border border-yellow-500/30">
                                        {product.category || 'Standard'}
                                    </div>
                                </div>
                                <div className="p-3 sm:p-4 flex flex-col flex-1">
                                    <h3 className="text-sm sm:text-lg font-bold text-white mb-2 line-clamp-2 flex-1">{product.name}</h3>
                                    <div className="flex justify-between items-end mt-2">
                                        <div>
                                            <p className="text-[10px] sm:text-xs text-gray-400 mb-1">Prix unitaire</p>
                                            <p className="text-base sm:text-xl font-extrabold text-yellow-500">{Number(product.price).toLocaleString()} FCFA</p>
                                        </div>
                                        <button 
                                            onClick={() => handleBuyClick(product)}
                                            className="px-3 py-1.5 sm:px-4 sm:py-2 rounded-xl bg-yellow-500 text-black font-bold text-xs sm:text-sm hover:scale-105 hover:shadow-[0_0_15px_rgba(234,179,8,0.5)] transition-all duration-300 shrink-0 ml-1"
                                        >
                                            Commander
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                
                {filteredProducts.length === 0 && !loading && (
                    <div className="text-center py-20 text-gray-400">
                        Aucun produit trouvé dans cette catégorie.
                    </div>
                )}

            </div>

            {/* Checkout Modal */}
            {isModalOpen && selectedProduct && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="bg-[#121212] border border-yellow-500/30 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden flex flex-col">
                        <div className="px-6 py-4 border-b border-gray-800 flex justify-between items-center bg-[#1a1a1a]">
                            <h3 className="text-xl font-bold text-white flex items-center gap-2">
                                <ShoppingCart className="text-yellow-500" /> Commander
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-white transition-colors">×</button>
                        </div>
                        
                        <div className="p-6 overflow-y-auto max-h-[80vh]">
                            {!user ? (
                                <div className="text-center py-8">
                                    <h4 className="text-xl font-bold text-white mb-4">Vous devez être connecté pour commander</h4>
                                    <div className="flex flex-col gap-3">
                                        <button onClick={() => navigate('/login')} className="w-full py-3 bg-yellow-500 hover:bg-yellow-400 text-black font-bold rounded-xl transition-colors">Se Connecter</button>
                                        <button onClick={() => navigate('/register')} className="w-full py-3 bg-[#1a1a1a] hover:bg-gray-800 border border-yellow-500/50 text-white font-bold rounded-xl transition-colors">Créer un compte</button>
                                    </div>
                                </div>
                            ) : orderSuccess ? (
                                <div className="text-center py-8">
                                    <div className="w-20 h-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-500/50">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" /></svg>
                                    </div>
                                    <h4 className="text-2xl font-bold text-white mb-2">Commande confirmée !</h4>
                                    <p className="text-gray-400">Nous vous contacterons très prochainement pour la livraison.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="bg-[#1a1a1a] p-4 rounded-xl mb-6 flex gap-4 items-center border border-gray-800">
                                        {selectedProduct.image_url ? (
                                            <img src={selectedProduct.image_url} alt="" className="w-16 h-16 object-cover rounded-lg" />
                                        ) : (
                                            <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center text-gray-500">
                                                <Package size={24} />
                                            </div>
                                        )}
                                        <div>
                                            <h4 className="text-white font-bold">{selectedProduct.name}</h4>
                                            <p className="text-yellow-500 font-bold text-lg">{Number(selectedProduct.price).toLocaleString()} FCFA</p>
                                        </div>
                                    </div>

                                    <form onSubmit={handleCheckoutSubmit} className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Prénom *</label>
                                                <input type="text" required className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-colors" value={checkoutForm.firstName} onChange={e => setCheckoutForm({...checkoutForm, firstName: e.target.value})} />
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-400 mb-1">Nom *</label>
                                                <input type="text" required className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-colors" value={checkoutForm.lastName} onChange={e => setCheckoutForm({...checkoutForm, lastName: e.target.value})} />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Téléphone *</label>
                                            <input type="tel" required className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-colors" value={checkoutForm.phone} onChange={e => setCheckoutForm({...checkoutForm, phone: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Email</label>
                                            <input type="email" className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-colors" value={checkoutForm.email} onChange={e => setCheckoutForm({...checkoutForm, email: e.target.value})} />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-400 mb-1">Adresse de livraison *</label>
                                            <textarea required rows={3} className="w-full px-4 py-3 bg-[#1a1a1a] border border-gray-800 rounded-lg text-white focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500 outline-none transition-colors resize-none" value={checkoutForm.address} onChange={e => setCheckoutForm({...checkoutForm, address: e.target.value})}></textarea>
                                        </div>
                                        <button 
                                            type="submit" 
                                            disabled={isSubmitting}
                                            className="w-full py-4 mt-4 bg-yellow-500 hover:bg-yellow-400 text-black font-extrabold text-lg rounded-xl transition-colors disabled:opacity-50"
                                        >
                                            {isSubmitting ? 'Validation...' : 'Valider la commande'}
                                        </button>
                                    </form>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ShopSection;
